import { useToast } from '@welbe/web-design-system';
import HappyFaceSvg from 'App/assets/img/happy-face.svg?url';
import SadFaceSvg from 'App/assets/img/sad-face.svg?url';
import { BrokersContext } from 'contexts/Brokers/Brokers';
import Analytics from 'helpers/analytics';
import { useFilters } from 'hooks/useFilters';
import {
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';
import { Candidate } from 'types';
import {
	getCompanyBusinessNames,
	postApproveCandidate,
} from './ModalApproveCandidate.services';

type ValidationError = {
	companyBadgeNumber?: string;
	businessName?: string;
	branchOfficeId?: string;
	hiringDate?: string;
	sector?: string;
	shift?: string;
	position?: string;
	insuranceId?: string;
};

type useApproveCandidateProps = {
	selectedCandidates: Candidate[];
	setSelectedCandidates: Dispatch<SetStateAction<Candidate[]>>;
	setReloadTable: Dispatch<SetStateAction<boolean>>;
	handleClose: () => void;
};

export const useModalApproveCandidate = ({
	selectedCandidates,
	setSelectedCandidates,
	setReloadTable,
	handleClose,
}: useApproveCandidateProps) => {
	const { toast } = useToast();
	const { isBroker } = useContext(BrokersContext);
	const [isCompanyBranchOfficesLoading, setCompanyBranchOfficesLoading] =
		useState<boolean>(false);
	const [companyBranchOffices, setCompanyBranchOffices] = useState<any[]>([]);
	const [showModalConfirm, setShowModalConfirm] = useState(false);
	const [indexApproveCandidate, setIndexApproveCandidate] = useState(0);
	const [isLoadingApproveCandidate, setLoadingApproveCandidate] =
		useState<boolean>(false);
	const [validationError, setValidationError] = useState<ValidationError>({
		branchOfficeId: '',
		insuranceId: '',
		businessName: '',
		companyBadgeNumber: '',
		hiringDate: '',
		sector: '',
		shift: '',
		position: '',
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		message: '',
		icon: '',
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onClose: () => {},
	});
	const {
		handleBranchOffice,
		handleInsurance,
		isLoadingBranchOffice,
		branchOfficeOptions,
		selectedBranchOffice,
		isLoadingInsurance,
		insuranceOptions,
		selectedInsurance,
		selectedBusinessName,
		setSelectedBusinessName,
	} = useFilters();

	const handleNextApproveCandidate = async () => {
		if (indexApproveCandidate < selectedCandidates.length - 1) {
			if (await handleApproveCandidates()) {
				handleBranchOffice({ id: '', value: 'Seleccione...' });
				handleInsurance({ id: '', value: 'Seleccione...' });
				handleInsurance({ id: '', value: 'Seleccione...' });
				setSelectedBusinessName({ id: '', value: '' });
				setIndexApproveCandidate(indexApproveCandidate + 1);
				setSelectedCandidates([...selectedCandidates]);
			}
		} else {
			if (await handleApproveCandidates()) {
				setConfirmModal({
					show: true,
					message: '¡Candidatos aprobado!',
					icon: HappyFaceSvg,
					onClose: () => {
						handleClose();
						setSelectedCandidates([]);
						setReloadTable(true);
					},
				});
			}
		}
	};

	const handleSelectedDate = (date: any) => {
		if (date) {
			selectedCandidates[indexApproveCandidate] = {
				...selectedCandidates[indexApproveCandidate],
				hiringDate: date,
			};
		}
	};

	const handleChangeInputs = (field: string, value: string) => {
		selectedCandidates[indexApproveCandidate] = {
			...selectedCandidates[indexApproveCandidate],
			[field]: value,
		};
		setSelectedCandidates([...selectedCandidates]);
	};

	const handlShowModalConfirm = () => {
		setShowModalConfirm(true);
	};

	const handleCloseModalConfirm = () => {
		setShowModalConfirm(false);
	};

	const handleExitApprove = () => {
		handleCloseModalConfirm();
		handleClose();
	};

	const validate = (): boolean => {
		if (
			selectedCandidates[indexApproveCandidate].companyBadgeNumber === '' ||
			selectedBranchOffice.id === ' ' ||
			selectedInsurance.id === ' ' ||
			selectedBusinessName.id === '' ||
			selectedCandidates[indexApproveCandidate].hiringDate === undefined ||
			selectedCandidates[indexApproveCandidate].shift === '' ||
			selectedCandidates[indexApproveCandidate].position === '' ||
			selectedCandidates[indexApproveCandidate].sector === ''
		) {
			setValidationError({
				...validationError,
				companyBadgeNumber:
					selectedCandidates[indexApproveCandidate].companyBadgeNumber === ''
						? '*Este campo es obligatorio'
						: '',
				branchOfficeId:
					selectedBranchOffice.id === ' ' ? '*Este campo es obligatorio' : '',
				insuranceId:
					selectedInsurance.id === ' ' ? '*Este campo es obligatorio' : '',
				businessName:
					selectedBusinessName?.id === '' ? '*Este campo es obligatorio' : '',
				hiringDate:
					selectedCandidates[indexApproveCandidate].hiringDate === undefined
						? '*Este campo es obligatorio'
						: '',
				shift:
					selectedCandidates[indexApproveCandidate].shift === ''
						? '*Este campo es obligatorio'
						: '',
				position:
					selectedCandidates[indexApproveCandidate].position === ''
						? '*Este campo es obligatorio'
						: '',
				sector:
					selectedCandidates[indexApproveCandidate].sector === ''
						? '*Este campo es obligatorio'
						: '',
			});
			return true;
		}

		return false;
	};

	const handleApproveCandidates = async () => {
		if (!validate()) {
			setLoadingApproveCandidate(true);
			try {
				const candidate = selectedCandidates[indexApproveCandidate];
				const payload = {
					branchOfficeId: selectedBranchOffice.id,
					businessName: selectedBusinessName,
					insuranceId: selectedInsurance.id,
					companyBadgeNumber: candidate.companyBadgeNumber,
					hiringDate: candidate.hiringDate,
					sector: candidate.sector,
					shift: candidate.shift,
					position: candidate.position,
				};
				await postApproveCandidate(candidate.id || '', payload);
				Analytics.track('candidate', 'Approve Candidates', {
					isBroker,
					branchOffice: selectedBranchOffice,
					insurance: selectedInsurance,
					candidate: selectedCandidates[indexApproveCandidate],
				});
				return true;
			} catch (error: any) {
				Analytics.track('candidate', 'Error Approve Candidates', {
					isBroker,
					branchOffice: selectedBranchOffice,
					insurance: selectedInsurance,
					candidate: selectedCandidates[indexApproveCandidate],
				});
				if (error.request.status === 400) {
					if (
						JSON.parse(error.request.response).message ===
						'Candidate already approved or rejected'
					) {
						setConfirmModal({
							show: true,
							message: 'El colaborador ya approvado or declinado.',
							icon: SadFaceSvg,
							onClose: () => {
								handleClose();
								setSelectedCandidates([]);
								setReloadTable(true);
							},
						});
					} else {
						if (
							JSON.parse(error.request.response).message ===
							'Company badge number already exists'
						) {
							setConfirmModal({
								show: true,
								message: 'El colaborador ya existe.',
								icon: SadFaceSvg,
								onClose: () => {
									handleClose();
									setSelectedCandidates([]);
									setReloadTable(true);
								},
							});
						} else {
							setConfirmModal({
								show: true,
								message:
									'Hubo un error, el candidato no pudo ser agregado, intentalo de nuevo.',
								icon: SadFaceSvg,
								onClose: () => {
									setConfirmModal({
										...confirmModal,
										show: false,
									});
								},
							});
						}
					}
				}
				return false;
			} finally {
				setLoadingApproveCandidate(false);
			}
		}
	};

	const fetchCompanyBranchOffices = async () => {
		setCompanyBranchOfficesLoading(true);
		try {
			const {
				data: { content },
			} = await getCompanyBusinessNames();
			if (content.length === 1) {
				setSelectedBusinessName(content[0]);
			}
			setCompanyBranchOffices(content);
		} catch (error) {
			toast.error('Hubo un error al consultar las razones sociales');
		} finally {
			setCompanyBranchOfficesLoading(false);
		}
	};

	useEffect(() => {
		fetchCompanyBranchOffices();
	}, []);

	return {
		showModalConfirm,
		indexApproveCandidate,
		isLoadingApproveCandidate,
		validationError,
		confirmModal,
		handlShowModalConfirm,
		handleCloseModalConfirm,
		handleNextApproveCandidate,
		handleSelectedDate,
		handleChangeInputs,
		handleExitApprove,
		handleBranchOffice,
		handleInsurance,
		isLoadingBranchOffice,
		branchOfficeOptions,
		selectedBranchOffice,
		isLoadingInsurance,
		insuranceOptions,
		selectedInsurance,
		isCompanyBranchOfficesLoading,
		companyBranchOffices,
		selectedBusinessName,
		setSelectedBusinessName,
	};
};
